import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  Tooltip,
} from "react-leaflet";
import L from "leaflet";
import axios from "axios";
import "leaflet/dist/leaflet.css";
import "./styles/Map.css";
import { useLocation } from "react-router-dom";
import debounce from "lodash/debounce";

import Detail from "./Detail";
import fullIcon from "/poleFull.png";
import emptyIcon from "/pole0.png";
import onlineIcon from "/status=Online,Type=Main.png";
import offlineIcon from "/status=Offline,Type=Main.png";
import Location from "/location1.png";

const Map = () => {
  const [locations, setLocations] = useState([]);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [mapCenter, setMapCenter] = useState(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);
  const [nearbyPole, setNearbyPole] = useState(null);
  const [filter, setFilter] = useState("all");
  const [highlightedMarker, setHighlightedMarker] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const fetchData = async () => {
      const branchName = sessionStorage.getItem("selectedBranch");
      if (!branchName) {
        console.error("No branch selected");
        return;
      }

      try {
        const response = await axios.get(
          `${import.meta.env.VITE_API_URL}/api/map/${branchName}`
        );
        const validData = response.data
          .filter((loc) => {
            const lat = parseFloat(loc.latitude);
            const lng = parseFloat(loc.longitude);
            return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
          })
          .map((loc) => ({
            ...loc,
            latitude: parseFloat(loc.latitude),
            longitude: parseFloat(loc.longitude),
          }));

        setLocations(validData);

        const masterLocation = validData.find((loc) => loc.isMaster);
        if (masterLocation) {
          setMapCenter([masterLocation.latitude, masterLocation.longitude]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const getIcon = useCallback(
    (location) => {
      let iconPath;
      let iconSize = [50, 50];
      let className = "";

      if (location.isMaster) {
        iconPath = location.active ? onlineIcon : offlineIcon;
      } else {
        iconPath = location.active ? fullIcon : emptyIcon;
      }

      if (
        highlightedMarker &&
        highlightedMarker.latitude === location.latitude &&
        highlightedMarker.longitude === location.longitude
      ) {
        className = "highlighted-marker";
      }

      return new L.DivIcon({
        html: `<div class="custom-icon ${className}"><img src="${iconPath}" /></div>`,
        iconSize: iconSize,
        iconAnchor: [25, 50],
        popupAnchor: [0, -50],
        className: "custom-marker",
      });
    },
    [highlightedMarker]
  );

  const handleCloseDetail = useCallback(() => {
    const lastMarker = selectedMarker;
    setSelectedMarker(null);
    setHighlightedMarker(lastMarker);

    setTimeout(() => {
      setHighlightedMarker(null);
    }, 2000);
  }, [selectedMarker]);

  const MapComponent = () => {
    const map = useMap();

    useEffect(() => {
      if (mapCenter) {
        map.flyTo(mapCenter, 15, {
          duration: 0.2,
        });
      }
    }, [mapCenter, map]);

    useEffect(() => {
      if (mapCenter && locations.length > 0) {
        let nearest = null;
        let minDistance = Infinity;

        locations.forEach((location) => {
          const distance = map.distance(mapCenter, [
            location.latitude,
            location.longitude,
          ]);
          if (distance < minDistance) {
            minDistance = distance;
            nearest = location;
          }
        });

        if (minDistance < 500) {
          setNearbyPole(nearest.id);
        } else {
          setNearbyPole(null);
        }
      }
    }, [mapCenter, locations, map]);

    return null;
  };

  const handleSelectMarker = useCallback((location) => {
    setMapCenter([location.latitude, location.longitude]);
    setSelectedMarker(location);
  }, []);

  const toggleSidebar = useCallback(() => {
    if (isSidebarCollapsed) {
      setIsHidden(false);
      setTimeout(() => {
        setIsSidebarCollapsed(false);
      }, 10);
    } else {
      setIsSidebarCollapsed(true);
      setTimeout(() => {
        setIsHidden(true);
      }, 300);
    }
  }, [isSidebarCollapsed]);

  const handleFilterChange = useCallback((newFilter) => {
    setFilter(newFilter);
  }, []);

  const handleSearchChange = useCallback(
    debounce((value) => {
      setSearchTerm(value);
    }, 300),
    []
  );

  const filteredLocations = useMemo(() => {
    return locations.filter((loc) => {
      const matchesSearchTerm = loc.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesFilter =
        filter === "all" ||
        (filter === "master" && loc.isMaster) ||
        (filter === "secondary" && !loc.isMaster);

      return matchesSearchTerm && matchesFilter;
    });
  }, [locations, searchTerm, filter]);

  return (
    <>
      {location.pathname === "/map" && (
        <div
          className={`map-sidebar-container ${
            isSidebarCollapsed ? "collapsed" : ""
          } ${isHidden ? "hidden" : ""} ${isSidebarHovered ? "hovered" : ""}`}
          onMouseEnter={() => setIsSidebarHovered(true)}
          onMouseLeave={() => setIsSidebarHovered(false)}
        >
          <input
            type="text"
            placeholder="ค้นหาจากชื่อเสา"
            onChange={(e) => handleSearchChange(e.target.value)}
            className="map-sidebar-search"
          />
          <div className="filter-buttons">
            {["all", "master", "secondary"].map((filterType) => (
              <button
                key={filterType}
                className={`filter-button ${
                  filter === filterType ? "active" : ""
                }`}
                onClick={() => handleFilterChange(filterType)}
              >
                {filterType === "all"
                  ? "เสาทั้งหมด"
                  : filterType === "master"
                  ? "เสาหลัก"
                  : "เสารอง"}
              </button>
            ))}
          </div>
          {filteredLocations.length === 0 ? (
            <p className="no-results">ไม่มีเสาที่ค้นหา</p>
          ) : (
            <ul className="map-sidebar-list">
              {filteredLocations.map((location, index) => (
                <li
                  key={`location-${location.id || index}`}
                  className={`map-sidebar-item ${
                    selectedMarker?.id === location.id ? "active" : ""
                  } ${nearbyPole === location.id ? "nearby" : ""}`}
                  onClick={() => handleSelectMarker(location)}
                >
                  <div className="location-name">
                    <strong>{location.name}</strong>
                    <label>{location.code}</label>
                  </div>
                  <div className="location-address">
                    <img src={Location} alt="Location icon" />{" "}
                    {location.location}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {location.pathname === "/map" && (
        <button
          className={`toggle-button ${isSidebarCollapsed ? "collapsed" : ""}`}
          onClick={toggleSidebar}
        >
          {isSidebarCollapsed ? "🔍︎" : ">"}
        </button>
      )}

      <MapContainer
        center={mapCenter || [13.7884053, 100.4278021]}
        zoom={15}
        style={{
          height: "100%",
          width: "99%",
          marginTop: "-50px",
          marginBottom: "-200px",
          borderRadius: "10px",
          overflow: "hidden",
        }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />

        <MapComponent />
        {filteredLocations.map((location, index) => (
          <Marker
            key={`marker-${location.id}-${location.latitude}-${location.longitude}-${index}`}
            position={[location.latitude, location.longitude]}
            icon={getIcon(location)}
            eventHandlers={{
              click: () => handleSelectMarker(location),
            }}
          >
            <Tooltip>{location.name}</Tooltip>
          </Marker>
        ))}
      </MapContainer>

      {selectedMarker && (
        <div className="noneshow">
          {" "}
          <Detail
            title="รายละเอียดเสา"
            weatherData={selectedMarker}
            onClose={handleCloseDetail}
            code={selectedMarker.code}
            name={selectedMarker.name}
            location={selectedMarker.location}
            lat={selectedMarker.latitude}
            lng={selectedMarker.longitude}
            sensors={selectedMarker.sensor}
            isMaster={selectedMarker.isMaster}
            masterId={selectedMarker.masterId}
            masterCode={selectedMarker.masterCode}
            screenCode={selectedMarker.screenId}
            screenModel={selectedMarker.screenModel}
            installDate={selectedMarker.screenInstallationDate}
            active={selectedMarker.active}
            hasScreen={selectedMarker.hasScreen}
          />
        </div>
      )}
    </>
  );
};

export default Map;
